import React, {CSSProperties, useEffect, useRef, useState} from "react"
import {AddToast} from "react-toast-notifications"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useEsc} from "../../hooks/useEsc"
import {useToolbar} from "../../hooks/useToolbar"
import {ApplicationDocumentModalAside} from "./ApplicationDocumentModalAside"
import {ImageTransformControls} from "../ImageTransformControls/ImageTransformControls"
import {ModalPdfNav} from "../Modals/ModalPdfNav"
import {ModalOppositeSideButton} from "./ModalOppositeSideButton"
import {Document, Page} from "react-pdf"
import "react-pdf/dist/Page/TextLayer.css"
import "react-pdf/dist/Page/AnnotationLayer.css"
import {QueryDispatch, SetStateAction} from "use-location-state"
import {ApplicationDocument, ApplicationDocumentSide, backSupportedDocumentTypes} from "../../resources/application"
import {DocumentableApplication, documentableGetDocumentLink} from "../../resources/documentable"

// Configure PDF.js worker and options (WASM for JPEG 2000 / OpenJPEG)
import "../../pdfWorker"
import {pdfDocumentOptions} from "../../pdfOptions"
import {downloadFromUrl, fileExtensionFromContentType} from "../../utilities/file"

function closeBlankFallbackTab(w: Window | null) {
	if (!w || w.closed) {
		return
	}
	try {
		if (w.location.href === "about:blank") {
			w.close()
		}
	} catch {
		// Navigated cross-origin; leave the tab open for the user.
	}
}

interface ApplicationDocumentModalProps {
	close: () => void
	application: DocumentableApplication
	document: ApplicationDocument
	accessToken: string
	documentSide: ApplicationDocumentSide
	refresh: () => void
	setDocSideQueryParam: QueryDispatch<SetStateAction<string>>
	addToast: AddToast
}

export function ApplicationDocumentModal(props: ApplicationDocumentModalProps) {
	const {close, application, document, accessToken, documentSide, refresh, setDocSideQueryParam, addToast} = props

	const {zoom, rotation, activeDocumentSide} = useToolbar(documentSide)
	const isBackSupportDocumentType = backSupportedDocumentTypes.includes(document.attributes.documentType)

	const [numPages, setNumPages] = useState(0)
	const [pageNumber, setPageNumber] = useState(1)
	const [isDownloading, setIsDownloading] = useState(false)
	const [manualOpenDocumentUrl, setManualOpenDocumentUrl] = useState<string | null>(null)
	const downloadInProgressRef = useRef(false)
	const isMountedRef = useRef(true)
	const downloadAbortControllerRef = useRef<AbortController | null>(null)
	const downloadFallbackTabRef = useRef<Window | null>(null)

	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
			downloadAbortControllerRef.current?.abort()
			// Do not close downloadFallbackTabRef here. In-flight downloads still need that
			// Window to assign location after await; React Strict Mode remount also runs this
			// cleanup and would close about:blank before navigation (user sees a stuck blank tab).
			// The download handler's branches + finally clear or navigate the tab.
		}
	}, [])

	const frontDocumentLinkState = useAsyncResult(() =>
		documentableGetDocumentLink(application, document.id, accessToken, ApplicationDocumentSide.Front)
	)
	const frontDocumentLink = frontDocumentLinkState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	const backDocumentLinkState = isBackSupportDocumentType
		? useAsyncResult(() =>
				documentableGetDocumentLink(application, document.id, accessToken, ApplicationDocumentSide.Back)
		  )
		: null
	const backDocumentLink = backDocumentLinkState
		? backDocumentLinkState.match(
				() => undefined,
				(image) => image,
				() => null
		  )
		: null

	const isFrontPdf = frontDocumentLink?.data.attributes.contentType.includes("pdf")
	const isBackPdf = backDocumentLink?.data.attributes.contentType.includes("pdf")

	const isActiveSidePdf =
		(activeDocumentSide.val === ApplicationDocumentSide.Front && isFrontPdf) ||
		(activeDocumentSide.val === ApplicationDocumentSide.Back && isBackPdf)

	const inlineImgStyle = {"--img-rotation": rotation.val + "deg", "--img-scale": zoom.val} as CSSProperties

	const handlePdfSuccess = ({numPages}: {numPages: number}) => setNumPages(numPages)

	const activeDocumentUrl =
		activeDocumentSide.val === ApplicationDocumentSide.Front
			? frontDocumentLink?.data.attributes.link
			: backDocumentLink?.data.attributes.link
	const activeContentType =
		activeDocumentSide.val === ApplicationDocumentSide.Front
			? frontDocumentLink?.data.attributes.contentType
			: backDocumentLink?.data.attributes.contentType

	useEffect(() => {
		setManualOpenDocumentUrl(null)
	}, [activeDocumentUrl])

	async function handleDownloadDocument() {
		if (!activeDocumentUrl || downloadInProgressRef.current) {
			return
		}
		downloadInProgressRef.current = true
		setIsDownloading(true)
		const abortController = new AbortController()
		downloadAbortControllerRef.current = abortController
		// Do not pass noopener here: browsers return null and we lose the Window handle needed
		// to navigate this tab when fetch-based download fails (e.g. CORS on signed storage URLs).
		// Clear opener after open so the navigated document cannot access this window (tabnabbing),
		// while we still retain the Window reference for location.replace.
		const fallbackWindow = window.open("about:blank", "_blank")
		if (fallbackWindow) {
			fallbackWindow.opener = null
		}
		downloadFallbackTabRef.current = fallbackWindow
		try {
			const contentType = activeContentType ?? "application/octet-stream"
			const ext = fileExtensionFromContentType(contentType)
			const filename = `${document.id}-${activeDocumentSide.val.toLowerCase()}.${ext}`
			const closeFallbackTab = () => {
				const w = downloadFallbackTabRef.current
				if (w && !w.closed) {
					w.close()
				}
				downloadFallbackTabRef.current = null
			}
			const showManualOpenHelp = (message: string) => {
				if (!isMountedRef.current) {
					return
				}
				setManualOpenDocumentUrl(activeDocumentUrl)
				addToast(message, {appearance: "warning"})
			}
			const ok = await downloadFromUrl(activeDocumentUrl, filename, contentType, abortController.signal)
			if (ok) {
				closeFallbackTab()
				if (isMountedRef.current) {
					setManualOpenDocumentUrl(null)
				}
			} else if (isMountedRef.current) {
				const w = downloadFallbackTabRef.current
				if (w && !w.closed) {
					try {
						w.location.replace(activeDocumentUrl)
						addToast("Opened the document in a new tab—you can save it from there.", {appearance: "info"})
					} catch {
						closeFallbackTab()
						showManualOpenHelp(
							"Could not open the document in a new tab. Use “Open document” below, or allow pop-ups for this site."
						)
					}
				} else {
					closeFallbackTab()
					showManualOpenHelp(
						"Pop-ups are blocked or unavailable. Use “Open document” below, or allow pop-ups for this site."
					)
				}
				downloadFallbackTabRef.current = null
			} else {
				closeFallbackTab()
			}
		} finally {
			downloadInProgressRef.current = false
			if (downloadAbortControllerRef.current === abortController) {
				downloadAbortControllerRef.current = null
			}
			closeBlankFallbackTab(downloadFallbackTabRef.current)
			downloadFallbackTabRef.current = null
			if (isMountedRef.current) {
				setIsDownloading(false)
			}
		}
	}

	useEsc(close)

	return (
		<div className="app-doc-modal modal is-active">
			<div className="modal-content">
				<div className="doc-wrapper">
					{frontDocumentLinkState.isOk() && (
						<>
							{manualOpenDocumentUrl && (
								<div className="doc-download-manual-open" role="status">
									<div className="doc-download-manual-open-inner">
										<span className="doc-download-manual-open-text">
											Download needs a new tab. If pop-ups are blocked, open the file here:
										</span>
										<a
											className="button is-small is-light doc-download-manual-open-link"
											href={manualOpenDocumentUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											Open document
										</a>
										<button
											type="button"
											className="button is-small is-ghost doc-download-manual-open-dismiss"
											onClick={() => setManualOpenDocumentUrl(null)}
										>
											Dismiss
										</button>
									</div>
								</div>
							)}
							{isActiveSidePdf ? (
								<Document
									options={pdfDocumentOptions}
									onLoadSuccess={handlePdfSuccess}
									file={
										activeDocumentSide.val === ApplicationDocumentSide.Front
											? frontDocumentLink?.data.attributes.link
											: backDocumentLink?.data.attributes.link
									}
								>
									<Page pageNumber={pageNumber} scale={zoom.val} rotate={rotation.val} />
								</Document>
							) : (
								<img
									className="doc-img"
									alt="document image"
									style={inlineImgStyle}
									src={
										activeDocumentSide.val === ApplicationDocumentSide.Front
											? frontDocumentLink?.data.attributes.link
											: backDocumentLink?.data.attributes.link
									}
								/>
							)}

							<footer aria-label="toolbar" className="toolbar">
								<ImageTransformControls
									leadingSlot={
										<button
											type="button"
											aria-label="Download document"
											title="Download document"
											disabled={!activeDocumentUrl || isDownloading}
											onClick={(e) => {
												e.stopPropagation()
												void handleDownloadDocument()
											}}
										>
											<svg
												width={20}
												height={20}
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth={2}
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden
											>
												<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
												<polyline points="7 10 12 15 17 10" />
												<line x1="12" y1="15" x2="12" y2="3" />
											</svg>
										</button>
									}
									changeRotation={rotation.changeRotation}
									zoomIn={zoom.zoomIn}
									zoomOut={zoom.zoomOut}
									zoomVal={zoom.val}
								/>

								{(isFrontPdf || isBackPdf) && numPages > 1 && (
									<ModalPdfNav pageNumber={pageNumber} numPages={numPages} setPageNumber={setPageNumber} />
								)}

								{isBackSupportDocumentType && !isFrontPdf && !isBackPdf && frontDocumentLink && backDocumentLink && (
									<ModalOppositeSideButton
										activeDocumentSide={activeDocumentSide.val}
										toggleActiveSide={activeDocumentSide.toggleActiveSide}
										backDocumentLink={backDocumentLink.data.attributes.link}
										frontDocumentLink={frontDocumentLink.data.attributes.link}
										setDocSideQueryParam={setDocSideQueryParam}
									/>
								)}
							</footer>
						</>
					)}
				</div>

				<ApplicationDocumentModalAside
					close={close}
					application={application}
					document={document}
					accessToken={accessToken}
					refresh={refresh}
					addToast={addToast}
				/>
			</div>
		</div>
	)
}
